import { MutableRefObject } from 'react';
import type { Editor } from '@tiptap/core';

import { setAttrsAtPos } from '../../extensions/listUtils';
import findNodeByFieldId from '../../utils/findNodeByFieldId';
import { DropdownOptionsStep } from './DropdownOptionsStep';

export const MAX_DROPDOWN_OPTION_LENGTH = 200;

export const NEW_DROPDOWN_OPTIONS = ['Option 1', 'Option 2'];

/** Copies a dropdown's options to its new field id, so the orphan plugin does not replace them with the selected value. */
export const carryDropdownOptions =
  (optionsRef?: MutableRefObject<Record<string, string[]>>) =>
  (previous: string | null, next: string): void => {
    const store = optionsRef?.current;
    if (!store || !previous) return;
    const options = store[previous];
    // The old id keeps its options: every further copy of the same field reads them from there.
    if (options) store[next] = [...options];
  };

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/documents/src/dropdownOptions.ts
// Checks a copied list as the save does, without the trimming an edit gets: accepted text stays as it was.
export const isValidDropdownOptions = (options: unknown): boolean =>
  Array.isArray(options) &&
  options.length > 0 &&
  options.every(
    (option) => typeof option === 'string' && !!option.trim() && option.length <= MAX_DROPDOWN_OPTION_LENGTH
  ) &&
  new Set(options.map((option: string) => option.trim())).size === options.length;

export const normalizeDropdownOption = (text: string): string =>
  text
    .replace(/<[^>]*>/g, '')
    .trim()
    .slice(0, MAX_DROPDOWN_OPTION_LENGTH);

/** A form field's choice edit: its options and its selected value change together, in one undo step. */
export const setDropdownOptions = (
  editor: Editor,
  optionsRef: MutableRefObject<Record<string, string[]>>,
  fieldId: string,
  options: string[],
  selectedValue: string
): void => {
  const found = findNodeByFieldId(editor, fieldId);
  if (!found) return;
  const { tr } = editor.state;
  const current = optionsRef.current[fieldId];
  // The dropdown plugin writes the options when the step applies; written here first, there would be nothing to undo.
  if (current?.length !== options.length || options.some((option, index) => option !== current[index])) {
    tr.step(DropdownOptionsStep.create(optionsRef.current, { [fieldId]: options }));
  }
  setAttrsAtPos(tr, found.pos, found.node.type, { selectedValue });
  editor.view.dispatch(tr);
};
